import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ConfirmationDialog,
  EditableTitle,
  PageLayout,
  Unauthorized,
} from "../../components";
import { CustomTabs } from "../../components/CustomTabs";
import { USER_GROUPS_QUERY_KEY } from "../../hooks/useAllUserGroupsForACompany";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useCompanyData } from "../../hooks/useCompanyData";
import { useCompanyDefaultUserGroups } from "../../hooks/useCompanyDefaultUserGroups";
import { useLocalization } from "../../hooks/useLocalization";
import { useTabNavigation } from "../../hooks/useTabNavigation";
import { EGroupTabs, Routes } from "../../types/NavigationPaths";
import { USER_GROUPS_BY_IDS_QUERY_KEY } from "../experiences/hooks/useExperienceUsers";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../experiences/hooks/useUsersForReleaseChannel";
import { ExperiencesTab } from "./ExperiencesTab/ExperiencesTab";
import {
  USER_GROUP_QUERY_KEY,
  useUserGroupById,
} from "./hooks/useUserGroupById";
import {
  UserGroupExperienceRow,
  useUserGroupExperiences,
} from "./hooks/useUserGroupExperiences";
import { useUserGroupExternalExperiences } from "./hooks/useUserGroupExternalExperiences";
import { useUsersInGroup } from "./hooks/useUsersInGroup";
import { OverviewTab } from "./OverviewTab/OverviewTab";
import { UsersTab } from "./UsersTab/UsersTab";

export const UserGroupDetails: React.FC = () => {
  const { groupId, companyId } = useParams<{ groupId: string; companyId: string }>();
  const { t } = useLocalization();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userManagementClient = useUserManagementServiceV1();
  const navigate = useNavigate();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.uam;

  const { userGroup, isLoading, error, refetchUserGroup } = useUserGroupById(groupId);

  const invalidateUserGroup = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [USER_GROUP_QUERY_KEY, groupId],
    });
  }, [groupId, queryClient]);

  const { users, isLoading: isUsersLoading } = useUsersInGroup({
    groupId,
  });

  const { refetchDefaultUserGroups } = useCompanyDefaultUserGroups(userGroup?.companyId, areHooksEnabled);

  const { isLoading: isCompanyLoading } = useCompanyData(userGroup?.companyId, areHooksEnabled);
  // Use the custom hook to get default user groups for the company
  const { defaultUserGroups } = useCompanyDefaultUserGroups(userGroup?.companyId, areHooksEnabled);

  const { experiences, isLoading: isExperiencesLoading } =
    useUserGroupExperiences({
      userGroupId: groupId,
      enabled: areHooksEnabled
    });

  const {
    experiences: externalExperiences,
    isLoading: isExternalExperiencesLoading,
  } = useUserGroupExternalExperiences({
    userGroupId: groupId,
    enabled: areHooksEnabled
  });

  const allExperiences = useMemo<UserGroupExperienceRow[]>(() => {
    return [...experiences, ...externalExperiences];
  }, [experiences, externalExperiences]);

  const isAllExperiencesLoading =
    isExperiencesLoading || isExternalExperiencesLoading;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const tabLabels = useMemo(
    () => ({
      [EGroupTabs.OVERVIEW]: t("groups.tabs.overview"),
      [EGroupTabs.USERS]: t("groups.tabs.users"),
      [EGroupTabs.EXPERIENCES]: t("groups.tabs.experiences"),
    }),
    [t],
  );

  const { activeTab, handleTabChange, tabItems } = useTabNavigation<EGroupTabs>(
    {
      tabEnum: EGroupTabs,
      defaultTab: EGroupTabs.OVERVIEW,
      labels: tabLabels,
    },
  );

  // Determine if this group is a primary/default group using the hook
  const isPrimaryGroup = useMemo(() => {
    if (!groupId) {
      return false;
    }

    return defaultUserGroups.some(
      (group) => group.userGroupId === groupId && group.isPrimaryGroup,
    );
  }, [defaultUserGroups, groupId]);

  // update group name logic
  const updateGroupMutation = useMutation({
    mutationFn: (editedUserGroup: UserManagement_V1.UserGroupWithoutId) => {
      if (!groupId) {
        return Promise.reject(
          new Error("Group ID is required to update the user group."),
        );
      }

      return userManagementClient.updateUserGroup(groupId, editedUserGroup);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_GROUPS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUP_QUERY_KEY, groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_BY_IDS_QUERY_KEY],
      });
    },
  });

  const handleSaveGroupName = async (newName: string) => {
    if (groupId && userGroup?.companyId) {
      await updateGroupMutation.mutateAsync(
        new UserManagement_V1.UserGroupWithoutId({
          userGroupName: newName,
          description: userGroup.description,
          companyId: userGroup.companyId,
        }),
      );
    }
  };

  const handleCancelEditGroupName = () => {
    updateGroupMutation.reset();
  };

  // delete group logic
  const deleteGroupMutation = useMutation({
    mutationFn: (targetGroupId: string) =>
      userManagementClient.deleteUserGroup(targetGroupId),
    onSuccess: (_, deletedGroupId) => {
      queryClient.setQueryData(
        [USER_GROUPS_QUERY_KEY, userGroup?.companyId],
        (oldData: UserManagement_V1.UserGroup[] | undefined) => {
          if (!oldData) return oldData;

          return oldData.filter(
            (group) => group.userGroupId !== deletedGroupId,
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USER_GROUPS_BY_IDS_QUERY_KEY],
      });

      setIsDeleteDialogOpen(false);
      void navigate(`/companies/${companyId}${Routes.GROUPS}`);
    },
  });

  const handleDeleteGroup = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (groupId) {
      deleteGroupMutation.mutate(groupId);
    }
  };

  const handleDeleteCancel = () => {
    deleteGroupMutation.reset();
    setIsDeleteDialogOpen(false);
  };

  const loading = isLoading || isCompanyLoading;

  if (error) {
    return (
      <PageLayout isLoading={loading}>
        <Typography color="error" variant="h6">
          {t("groups.errorLoadingGroup", { message: error.message })}
        </Typography>
      </PageLayout>
    );
  }

  if (!loading && !access.uam) {
    return (<Unauthorized />)
  }

  return (
    <PageLayout isLoading={loading}>
      <Stack spacing={{ xs: 1, md: 2 }}>
        {userGroup?.userGroupName && (
          <EditableTitle
            disabled={updateGroupMutation.isPending}
            error={!!updateGroupMutation.error}
            errorMessage={getRenameGroupErrorMessage(
              updateGroupMutation.error,
              t,
            )}
            fontSize={24}
            fontWeight={700}
            isEditable={!isPrimaryGroup}
            maxLength={28}
            onCancel={handleCancelEditGroupName}
            onSave={handleSaveGroupName}
            placeholder={t("groups.enterGroupName")}
            textFieldMaxWidth={408}
            value={userGroup.userGroupName}
          />
        )}

        <CustomTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabItems}
        />
        <Box sx={{ paddingTop: { xs: 2, md: 0 } }}>
          {activeTab === EGroupTabs.OVERVIEW && (
            <OverviewTab
              groupId={groupId}
              invalidateUserGroup={invalidateUserGroup}
              isPrimaryGroup={isPrimaryGroup}
              onDeleteGroup={handleDeleteGroup}
              refetchDefaultUserGroups={refetchDefaultUserGroups}
              refetchUserGroup={refetchUserGroup}
              userGroup={userGroup}
            />
          )}
          {activeTab === EGroupTabs.USERS && (
            <UsersTab
              companyId={userGroup?.companyId}
              groupId={userGroup?.userGroupId}
              isLoading={isUsersLoading}
              isPrimaryGroup={isPrimaryGroup}
              userGroupName={userGroup?.userGroupName}
              users={users}
            />
          )}
          {activeTab === EGroupTabs.EXPERIENCES && (
            <ExperiencesTab
              experiences={allExperiences}
              groupId={userGroup?.userGroupId}
              isLoading={isAllExperiencesLoading}
              userGroupName={userGroup?.userGroupName}
            />
          )}
        </Box>
      </Stack>
      <ConfirmationDialog.Root
        aria-describedby="delete-dialog-description"
        aria-labelledby="delete-dialog-title"
        maxWidth="xs"
        onClose={handleDeleteCancel}
        open={isDeleteDialogOpen}
      >
        <ConfirmationDialog.Title
          iconColor={theme.colors.orange}
          id="delete-dialog-title"
          showIcon={true}
        >
          {t("groups.deleteGroup")}
        </ConfirmationDialog.Title>
        <ConfirmationDialog.Content error={deleteGroupMutation.error}>
          <ConfirmationDialog.Text id="delete-dialog-description">
            <Trans
              components={{ strong: <strong /> }}
              i18nKey="groups.deleteGroupConfirmation"
              values={{ groupName: userGroup?.userGroupName }}
            />
          </ConfirmationDialog.Text>
        </ConfirmationDialog.Content>
        <ConfirmationDialog.Actions>
          <ConfirmationDialog.Button
            onClick={handleDeleteCancel}
            variant="contained"
          >
            {t("groups.cancel")}
          </ConfirmationDialog.Button>
          <ConfirmationDialog.Button
            isLoading={deleteGroupMutation.isPending}
            loadingText={t("groups.deleting")}
            onClick={handleDeleteConfirm}
            variant="secondary"
          >
            {t("groups.delete")}
          </ConfirmationDialog.Button>
        </ConfirmationDialog.Actions>
      </ConfirmationDialog.Root>
    </PageLayout>
  );
};

const getRenameGroupErrorMessage = (
  error?: Error | null,
  t?: (key: string) => string,
) => {
  if (!error || !t) return undefined;

  const castedError = error as Error & { status?: number };

  // Check if it's a 409 Conflict error
  if (
    castedError.status === 409 ||
    castedError.message.includes("409") ||
    castedError.message.toLowerCase().includes("conflict")
  ) {
    return t("groups.groupNameInUse");
  }

  return t("groups.renameGroupFailed");
};
